/**
 * CLEANING / SERVERS (F5-residual) — PURE shift-roster logic.
 *
 * A group in the `cleaning` category organizes CLEANING SHIFTS (`cleaning_shift`,
 * by date and optionally by location) and ASSIGNS people to each shift
 * (`cleaning_assignment`). Everything here is INDEPENDENT of the DB and the
 * framework: state normalization, shift ordering, and FAIR ROTATION of
 * people across shifts (fair round-robin). It is tested with `bun
 * test`. The logic is NEVER coupled to the group's own name, only to the
 * category/capability (gotcha products/agape.md §9).
 */

export const CLEANING_SHIFT_STATUSES = ["pending", "done"] as const;
export type CleaningShiftStatus = (typeof CLEANING_SHIFT_STATUSES)[number];

/** Normaliza un `status` de texto libre a un estado conocido (default `pending`). */
export function toShiftStatus(status: string | null | undefined): CleaningShiftStatus {
  return status === "done" ? "done" : "pending";
}

/** Structural row for a shift (what the pure logic uses). */
export interface CleaningShiftLike {
  id: string;
  shift_date?: string | null;
  status?: string | null;
  created_at?: string | null;
  deleted_at?: string | null;
}

/** Structural row for a person↔shift assignment. */
export interface CleaningAssignmentLike {
  id: string;
  shift_id: string | null;
  person_id: string | null;
}

function aliveShifts<T extends CleaningShiftLike>(shifts: readonly T[]): T[] {
  return shifts.filter((s) => !s.deleted_at);
}

/** Is the shift marked as done? */
export function isShiftDone(shift: CleaningShiftLike): boolean {
  return toShiftStatus(shift.status) === "done";
}

/**
 * Visible shifts ordered by ascending date (undated shifts last).
 * With `fromDate` (YYYY-MM-DD), filters to that date onward — useful for
 * "upcoming shifts". Lexicographic comparison: valid for ISO YYYY-MM-DD.
 */
export function upcomingShifts<T extends CleaningShiftLike>(
  shifts: readonly T[],
  fromDate?: string | null,
): T[] {
  return aliveShifts(shifts)
    .filter((s) => !fromDate || (s.shift_date ?? "￿") >= fromDate)
    .toSorted(
      (a, b) =>
        (a.shift_date ?? "￿").localeCompare(b.shift_date ?? "￿") ||
        (a.created_at ?? "").localeCompare(b.created_at ?? ""),
    );
}

/** Asignaciones de un turno concreto. */
export function assignmentsOfShift<T extends CleaningAssignmentLike>(
  assignments: readonly T[],
  shiftId: string,
): T[] {
  return assignments.filter((a) => a.shift_id === shiftId);
}

/** Does the shift have at least one assigned person? (covered shift). */
export function shiftIsCovered(
  assignments: readonly CleaningAssignmentLike[],
  shiftId: string,
): boolean {
  return assignments.some((a) => a.shift_id === shiftId);
}

export interface RotationOptions {
  /** People per shift (default: 1). Limited to the number of available people. */
  perShift?: number;
  /** Starting index in the people list (to continue a rotation). */
  startIndex?: number;
}

export interface RosterAssignment {
  shiftId: string;
  personId: string;
}

/**
 * FAIR ROTATION: distributes the `personIds` across the `shiftIds` in
 * round-robin order. The cursor advances CONTINUOUSLY across shifts, so
 * the load stays balanced (no one repeats until everyone has been assigned).
 * Never assigns the same person twice WITHIN THE SAME shift.
 */
export function rotateRoster(
  personIds: readonly string[],
  shiftIds: readonly string[],
  opts: RotationOptions = {},
): RosterAssignment[] {
  const out: RosterAssignment[] = [];
  if (personIds.length === 0 || shiftIds.length === 0) {
    return out;
  }
  const perShift = Math.max(1, Math.trunc(opts.perShift ?? 1));
  const takePerShift = Math.min(perShift, personIds.length);
  const size = personIds.length;
  let cursor = (((opts.startIndex ?? 0) % size) + size) % size;

  for (const shiftId of shiftIds) {
    const seen = new Set<string>();
    let assigned = 0;
    let steps = 0;
    // `steps < size` bounds loop (one full round visits everyone); in that
    // vuelta se recogen `takePerShift` (≤ size) personas distintas.
    while (assigned < takePerShift && steps < size) {
      const personId = personIds[cursor];
      cursor = (cursor + 1) % size;
      steps += 1;
      if (personId === undefined || seen.has(personId)) {
        continue;
      }
      seen.add(personId);
      out.push({ shiftId, personId });
      assigned += 1;
    }
  }
  return out;
}

export interface RosterBalance {
  counts: Record<string, number>;
  min: number;
  max: number;
  /** Difference between the person with the most and fewest shifts (0 = perfect). */
  spread: number;
}

/**
 * Roster load balance: how many shifts each person has. Includes ALL
 * `personIds` (even those with 0) so `spread` reflects anyone who
 * has not yet entered the rotation.
 */
export function rosterBalance(
  assignments: readonly CleaningAssignmentLike[],
  personIds: readonly string[],
): RosterBalance {
  const counts: Record<string, number> = {};
  for (const personId of personIds) {
    counts[personId] = 0;
  }
  for (const a of assignments) {
    if (!a.person_id) {
      continue;
    }
    counts[a.person_id] = (counts[a.person_id] ?? 0) + 1;
  }
  const values = personIds.map((p) => counts[p] ?? 0);
  const min = values.length > 0 ? Math.min(...values) : 0;
  const max = values.length > 0 ? Math.max(...values) : 0;
  return { counts, min, max, spread: max - min };
}

/**
 * Next person in the rotation: whoever has the FEWEST shifts (ties → the
 * order of `personIds`). Used to suggest whom to assign to the next shift.
 */
export function nextInRotation(
  personIds: readonly string[],
  assignments: readonly CleaningAssignmentLike[],
): string | null {
  const [first] = personIds;
  if (first === undefined) {
    return null;
  }
  const { counts } = rosterBalance(assignments, personIds);
  let best = first;
  for (const personId of personIds) {
    if ((counts[personId] ?? 0) < (counts[best] ?? 0)) {
      best = personId;
    }
  }
  return best;
}
